/**
 * API Service
 * 
 * Handles all API requests to the backend.
 * 
 * ENVIRONMENTS:
 * - Local: Uses Next.js rewrites to proxy to http://localhost:3001/api
 * - Vercel: Uses same origin /api (handled by serverless function)
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

class ApiService {
  private baseURL: string;
  private token: string | null;

  private trackingIds: Record<string, string> = {};
  private createTrackingId(prefix: string): string {
    if (this.trackingIds[prefix]) {
      return this.trackingIds[prefix];
    }
    const hasCrypto = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
    let id;
    if (hasCrypto) {
      id = `${prefix}_${crypto.randomUUID()}`;
    } else {
      id = `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }
    this.trackingIds[prefix] = id;
    return id;
  }

  private getTrackingIdentifiers(): { deviceId: string; sessionId: string } {
    const fallback = {
      deviceId: this.createTrackingId('device'),
      sessionId: this.createTrackingId('session'),
    };

    if (typeof window === 'undefined') {
      return fallback;
    }

    try {
      const deviceStorageKey = 'swasti_device_id';
      const sessionStorageKey = 'swasti_session_id';

      let deviceId = localStorage.getItem(deviceStorageKey);
      if (!deviceId) {
        deviceId = this.createTrackingId('device');
        localStorage.setItem(deviceStorageKey, deviceId);
      }

      let sessionId = sessionStorage.getItem(sessionStorageKey);
      if (!sessionId) {
        sessionId = this.createTrackingId('session');
        sessionStorage.setItem(sessionStorageKey, sessionId);
      }

      return { deviceId, sessionId };
    } catch {
      // Storage may be unavailable in restricted browser modes.
      return fallback;
    }
  }

  private getDeviceInfo(): { deviceType: string; platform: string; os: string; browser: string } {
    if (typeof window === 'undefined') {
      return {
        deviceType: 'unknown',
        platform: 'server',
        os: 'unknown',
        browser: 'unknown',
      };
    }

    const ua = navigator.userAgent || '';
    const platform = (navigator.platform || 'unknown').toLowerCase();
    const uaLower = ua.toLowerCase();

    const deviceType = /ipad|tablet/.test(uaLower)
      ? 'tablet'
      : /mobile|android|iphone|ipod/.test(uaLower)
      ? 'mobile'
      : /bot|crawler|spider|crawling/.test(uaLower)
      ? 'bot'
      : 'desktop';

    const os = /windows/.test(uaLower)
      ? 'Windows'
      : /mac os|macintosh/.test(uaLower)
      ? 'macOS'
      : /android/.test(uaLower)
      ? 'Android'
      : /iphone|ipad|ios/.test(uaLower)
      ? 'iOS'
      : /linux/.test(uaLower)
      ? 'Linux'
      : platform || 'unknown';

    const browser = /edg\//.test(uaLower)
      ? 'Edge'
      : /chrome\//.test(uaLower) && !/edg\//.test(uaLower)
      ? 'Chrome'
      : /safari\//.test(uaLower) && !/chrome\//.test(uaLower)
      ? 'Safari'
      : /firefox\//.test(uaLower)
      ? 'Firefox'
      : /opr\//.test(uaLower)
      ? 'Opera'
      : 'Unknown';

    return {
      deviceType,
      platform: navigator.platform || 'unknown',
      os,
      browser,
    };
  }

  constructor() {
    // Determine base URL based on environment
    if (typeof window !== 'undefined') {
      // Client-side (browser)
      const isDevelopment = process.env.NODE_ENV === 'development';
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const isVercel = process.env.VERCEL || window.location.hostname.includes('vercel.app') || window.location.hostname.includes('swastishanti.com');
      
      if (isDevelopment && isLocalhost && !isVercel) {
        // Local development: Use Next.js rewrite proxy (localhost:3000/api -> localhost:3001/api)
        this.baseURL = '/api';
      } else {
        // Production/Vercel/Live site: Use same origin (serverless function handles it)
        // This works for both Vercel deployment and production domain
        this.baseURL = '/api';
      }
      
      // Load token from localStorage on initialization
      this.token = localStorage.getItem('authToken');
      // Ensure already-logged-in sessions also publish the shared subdomain
      // cookie (users who logged in before this feature existed).
      if (this.token) this.setSharedAuthCookie(this.token);

      if (process.env.NODE_ENV === 'development') {
        console.log('🌐 API Base URL:', this.baseURL);
        console.log('🔑 Token loaded:', this.token ? 'Yes' : 'No');
      }
    } else {
      // Server-side rendering.
      // CRITICAL: SSR fetches must go DIRECTLY to the local backend, never to
      // the public domain. When NEXT_PUBLIC_API_URL pointed at
      // https://swastishanti.com/api, every server-rendered page fetched its
      // own public URL — out through nginx and back to this same machine —
      // and regularly hit the 10 s timeout, making the site appear down.
      // Resolution order:
      // 1) INTERNAL_API_URL   — explicit internal override (e.g. http://127.0.0.1:3002/api)
      // 2) BACKEND_URL        — same env the next.config.js rewrite already uses
      // 3) VERCEL_URL         — Vercel serverless (no local backend exists there)
      // 4) NEXT_PUBLIC_API_URL, but ONLY if it is a local address
      // 5) http://127.0.0.1:3002/api — the production backend port
      const isLocalUrl = (u: string) => u.includes('localhost') || u.includes('127.0.0.1');
      const internalUrl = process.env.INTERNAL_API_URL;
      const backendUrl = process.env.BACKEND_URL;
      const vercelUrl = process.env.VERCEL_URL;
      const publicUrl = process.env.NEXT_PUBLIC_API_URL;
      if (internalUrl) {
        this.baseURL = internalUrl;
      } else if (backendUrl) {
        this.baseURL = `${backendUrl.replace(/\/$/, '')}/api`;
      } else if (vercelUrl) {
        this.baseURL = `https://${vercelUrl}/api`;
      } else if (publicUrl && isLocalUrl(publicUrl)) {
        this.baseURL = publicUrl;
      } else {
        this.baseURL = 'http://127.0.0.1:3002/api';
      }
      this.token = null;
    }
  }

  setToken(token: string | null) {
    this.token = token;
    if (token && typeof window !== 'undefined') {
      localStorage.setItem('authToken', token);
      this.setSharedAuthCookie(token);
    } else if (typeof window !== 'undefined') {
      localStorage.removeItem('authToken');
      this.setSharedAuthCookie(null);
    }
  }

  // Share the auth token across every *.swastishanti.com subdomain via a
  // cookie on the parent domain, so logging in on swastishanti.com also logs
  // the user in on kundali.swastishanti.com (and vice-versa). Not httpOnly by
  // necessity (read client-side on the subdomain) — same exposure as the
  // existing localStorage token. Skipped on localhost, where a
  // .swastishanti.com cookie can't be set.
  private setSharedAuthCookie(token: string | null) {
    try {
      const host = window.location.hostname;
      if (!host.endsWith('swastishanti.com')) return;
      if (token) {
        const maxAge = 7 * 24 * 60 * 60; // 7 days
        document.cookie = `sw_token=${encodeURIComponent(token)}; Domain=.swastishanti.com; Path=/; Max-Age=${maxAge}; Secure; SameSite=Lax`;
      } else {
        document.cookie = `sw_token=; Domain=.swastishanti.com; Path=/; Max-Age=0; Secure; SameSite=Lax`;
      }
    } catch {
      /* cookie API unavailable — ignore */
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    config: { timeoutMs?: number; disableTimeout?: boolean } = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const controller = new AbortController();
    const isLocalHost = this.baseURL.includes('localhost') || this.baseURL.includes('127.0.0.1');
    // Timeout: 15 seconds for local dev, 10 seconds for production (unless overridden)
    const defaultTimeout = isLocalHost ? 15000 : 10000;
    const timeout = config.timeoutMs ?? defaultTimeout;
    const useTimeout = !config.disableTimeout;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      if (useTimeout) {
        timeoutId = setTimeout(() => {
          console.error('⏱️ Request timeout - aborting after', timeout, 'ms');
          controller.abort();
        }, timeout);
      }

      console.log('📡 Making fetch request:', {
        url,
        method: options.method || 'GET',
        hasToken: !!this.token
      });

      const response = await fetch(url, {
        ...options,
        headers: headers as HeadersInit,
        signal: controller.signal,
        // Cache strategy for different endpoints
        ...(endpoint.includes('/calendar/current')
          ? { cache: 'no-store' as RequestCache } // Always fresh — date must not be stale
          : endpoint.includes('/calendar/month-grid')
          ? { cache: 'no-store' as RequestCache }
          : {}),
      });

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      console.log('📥 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData: any = {};
        const isPanchanga404 = response.status === 404 && endpoint.includes('/panchanga');
        try {
          // Read body as text first to avoid "Body already read" error
          const text = await response.text();
          if (text) {
            try {
              errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
              if (!isPanchanga404) {
                console.error('❌ API Error Response:', JSON.stringify(errorData));
              }
            } catch {
              // Not valid JSON, use text as error message
              errorMessage = text.substring(0, 200);
              if (!isPanchanga404) {
                console.error('❌ API Error (non-JSON):', text.substring(0, 200));
              }
            }
          }
        } catch (bodyError) {
          if (!isPanchanga404) {
            console.error('❌ Could not read error response body:', bodyError);
          }
        }
        
        // Handle 401 Unauthorized - token is invalid or expired
        if (response.status === 401) {
          // Clear invalid token
          if (typeof window !== 'undefined') {
            this.setToken(null);
          }
          throw new Error('Unauthorized: Please login again');
        }
        
        // Provide more specific error messages
        if (response.status === 404) {
          // For panchanga endpoint, return empty data instead of throwing error
          // This allows the UI to handle missing data gracefully
          if (endpoint.includes('/panchanga')) {
            console.warn(`⚠️ Panchanga data not found for endpoint: ${endpoint}`);
            return { success: false, data: null, message: 'Panchanga data not available for this date' } as T;
          }

          // If backend provided a business message (e.g., no account found), preserve it.
          if (errorData?.message && typeof errorData.message === 'string') {
            const notFoundError = new Error(errorData.message);
            (notFoundError as any).response = {
              status: response.status,
              statusText: response.statusText,
              data: errorData,
            };
            (notFoundError as any).isBackendHandled404 = true;
            throw notFoundError;
          }
          
          // For blog endpoints, provide more helpful error message
          if (endpoint.includes('/blog')) {
            const isLocalhost = this.baseURL.includes('localhost') || this.baseURL.includes('127.0.0.1');
            if (isLocalhost) {
              throw new Error(`Blog API endpoint not found: ${endpoint}. Please ensure the backend server is running on port 3001. Start it with: cd swasti-shanti-prod/backend && npm start`);
            } else {
              throw new Error(`Blog API endpoint not found: ${endpoint}. Please check server configuration.`);
            }
          }
          
          const isLocalhost = this.baseURL.includes('localhost') || this.baseURL.includes('127.0.0.1');
          if (isLocalhost) {
            throw new Error(`API endpoint not found. The endpoint ${endpoint} does not exist or the backend server is not running. Start it with: cd backend && npm start`);
          } else {
            throw new Error(`API endpoint not found: ${endpoint}`);
          }
        }
        
        // Handle 500 errors with "API routes not available" message
        if (response.status === 500 && errorData?.message?.includes('API routes not available')) {
          const isLocalhost = this.baseURL.includes('localhost') || this.baseURL.includes('127.0.0.1');
          if (isLocalhost) {
            throw new Error(`API routes not available. The backend server may not be running or there's a syntax error in the routes. Check backend logs. Error: ${errorData.error || errorData.message}`);
          } else {
            throw new Error(`API routes not available. Please check server logs for details. Error: ${errorData.error || errorData.message}`);
          }
        }
        
        const error = new Error(errorMessage);
        (error as any).response = {
          status: response.status,
          statusText: response.statusText,
          data: errorData,
        };
        throw error;
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        return text ? JSON.parse(text) : ({} as T);
      }

      return {} as T;
    } catch (error: any) {
      // Handle network errors, timeouts, etc.
      console.error('❌ Fetch error:', error.name || 'Unknown', error.message || 'Unknown error', url);
      
      if (useTimeout && error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout: The server took too long to respond. Please check your database connection and try refreshing the page.');
        (timeoutError as any).isTimeout = true;
        console.error('⏱️ Request timed out after', timeout, 'ms');
        throw timeoutError;
      }
      
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        console.error('🌐 Network error - backend server may not be running');
        // Check if we're in production by checking window.location (client-side) or environment (server-side)
        const isProduction = typeof window !== 'undefined' 
          ? !(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          : process.env.NODE_ENV === 'production';
        
        if (isProduction) {
          throw new Error('Cannot connect to server. The API service may be temporarily unavailable. Please try again later or contact support if the issue persists.');
        } else {
          throw new Error('Cannot connect to server. Please ensure the backend server is running on port 3001.');
        }
      }
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // Check if we're in production
        const isProduction = typeof window !== 'undefined' 
          ? !(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          : process.env.NODE_ENV === 'production';
        
        if (isProduction) {
          throw new Error(`API service unavailable. Please check your internet connection and try again. If the problem persists, the server may be experiencing issues.`);
        } else {
          throw new Error(`API endpoint not found. Cannot connect to backend server at ${this.baseURL}. Please ensure the backend server is running. Start it with: cd backend && npm start`);
        }
      }
      // Check for 404 errors specifically
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        if ((error as any).isBackendHandled404) {
          throw error;
        }

        const isLocalhost = this.baseURL.includes('localhost') || this.baseURL.includes('127.0.0.1');
        const errorDetails = `Endpoint: ${endpoint}\nFull URL: ${url}\nBase URL: ${this.baseURL}`;
        console.error('❌ 404 Error Details:', errorDetails);
        
        if (isLocalhost) {
          throw new Error(`API endpoint not found: ${endpoint}\n\nThe requested endpoint does not exist or the backend server is not running.\n\nPlease ensure:\n1. Backend server is running on port 3001\n2. Start it with: cd swasti-shanti-prod/backend && npm start\n3. Check that the endpoint ${endpoint} exists in the backend routes`);
        } else {
          throw new Error(`API endpoint not found: ${endpoint}\n\nThe requested endpoint does not exist or the server is not running.\n\nFull URL: ${url}`);
        }
      }
      throw error;
    }
  }

  // Calendar endpoints
  async getCurrentDate() {
    return this.request('/calendar/current');
  }

  async getMonthGrid(year?: number, month?: number) {
    const params = new URLSearchParams();
    if (year) params.append('nepaliYear', year.toString());
    if (month) params.append('nepaliMonth', month.toString());
    return this.request(`/calendar/month-grid?${params.toString()}`);
  }

  // Events endpoints
  async getEvents(year?: number, month?: number) {
    const params = new URLSearchParams();
    if (year) params.append('nepaliYear', year.toString());
    if (month) params.append('nepaliMonth', month.toString());
    return this.request(`/calendar/events?${params.toString()}`);
  }

  // Calendar cell data endpoints
  async getCalendarCellData(year?: number, month?: number) {
    const params = new URLSearchParams();
    if (year) params.append('year', String(year));
    if (month) params.append('month', String(month).padStart(2, '0'));
    return this.request(`/calendar-cell-data?${params.toString()}`);
  }

  async createCalendarCellData(data: any) {
    return this.request('/calendar-cell-data', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCalendarCellData(id: number, data: any) {
    return this.request(`/calendar-cell-data/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCalendarCellData(id: number) {
    return this.request(`/calendar-cell-data/${id}`, {
      method: 'DELETE',
    });
  }

  async autoFillCalendarCellData(year: number, month: number) {
    return this.request('/calendar-cell-data/auto-fill', {
      method: 'POST',
      body: JSON.stringify({ year, month }),
    });
  }

  // Ceremony muhurta (Agni/Rudri, Annaprashan, Griha Aarambha, Griha Prabesh)
  async getCeremonyMuhurta(params: { ceremony?: string; year?: number; month?: number; date?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.ceremony) qs.set('ceremony', params.ceremony);
    if (params.year) qs.set('year', String(params.year));
    if (params.month) qs.set('month', String(params.month).padStart(2, '0'));
    if (params.date) qs.set('date', params.date);
    const q = qs.toString();
    return this.request(`/ceremony-muhurta${q ? '?' + q : ''}`);
  }

  async getCeremonyMuhurtaByDate(nepaliDate: string) {
    return this.request(`/ceremony-muhurta/date/${nepaliDate}`);
  }

  async createCeremonyMuhurta(data: any) {
    return this.request('/ceremony-muhurta', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateCeremonyMuhurta(id: number, data: any) {
    return this.request(`/ceremony-muhurta/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteCeremonyMuhurta(id: number) {
    return this.request(`/ceremony-muhurta/${id}`, { method: 'DELETE' });
  }

  // Banner / ad-slide endpoints — one banner can be placed on multiple pages,
  // each placement carrying its own size and order.
  async getBanners(page: string) {
    return this.request(`/banners?page=${encodeURIComponent(page)}`);
  }

  async getBannerImage(id: number) {
    return this.request(`/banners/${id}/image`);
  }

  async getBannerIconImage(id: number) {
    return this.request(`/banners/${id}/icon-image`);
  }

  async getAllBannersAdmin() {
    return this.request('/banners/admin/all');
  }

  async createBanner(data: any) {
    return this.request('/banners', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateBanner(id: number, data: any) {
    return this.request(`/banners/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteBanner(id: number) {
    return this.request(`/banners/${id}`, { method: 'DELETE' });
  }

  // Festivals endpoints
  async getFestivals(year?: number) {
    const params = year ? `?year=${year}` : '';
    return this.request(`/festivals${params}`);
  }

  async getFestivalByDate(date: string) {
    return this.request(`/festivals/${date}`);
  }

  // Date conversion endpoints
  async convertDate(from: 'nepali' | 'english', to: 'nepali' | 'english', date: string) {
    const params = new URLSearchParams({ from, to, date });
    return this.request(`/calendar/convert?${params.toString()}`);
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.request<{ success: boolean; token?: string; user?: any; message?: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (response.success && response.token) {
      this.setToken(response.token);
    }
    return response;
  }

  async signup(userData: { name: string; email: string; password: string; phone?: string; country?: string }) {
    const response = await this.request<{ success: boolean; token?: string; user?: any; message?: string }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (response.success && response.token) {
      this.setToken(response.token);
    }
    return response;
  }

  async logout() {
    this.setToken(null);
  }

  async googleLogin(idToken: string) {
    const response = await this.request<{ success: boolean; token?: string; user?: any; message?: string }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });
    if (response.success && response.token) {
      this.setToken(response.token);
    }
    return response;
  }

  async getProfile() {
    return this.request<{ success: boolean; data?: any; message?: string }>('/auth/profile');
  }

  async updateProfile(data: Record<string, any>) {
    return this.request<{ success: boolean; data?: any; message?: string }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async requestEmailVerification(newEmail?: string) {
    return this.request<{ success: boolean; message?: string }>('/auth/profile/email/request-verification', {
      method: 'POST',
      body: JSON.stringify(newEmail ? { newEmail } : {}),
    });
  }

  async verifyEmailChange(token: string) {
    return this.request<{ success: boolean; data?: any; message?: string }>('/auth/profile/email/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async requestPhoneOtp(phone: string) {
    return this.request<{ success: boolean; message?: string }>('/auth/profile/phone/request-otp', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
  }

  async verifyPhoneOtp(otp: string) {
    return this.request<{ success: boolean; data?: any; message?: string }>('/auth/profile/phone/verify', {
      method: 'POST',
      body: JSON.stringify({ otp }),
    });
  }

  async requestPasswordChangeCode() {
    return this.request<{ success: boolean; message?: string }>('/auth/profile/password/request-code', {
      method: 'POST',
    });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ success: boolean; message?: string }>('/auth/profile/password/change', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async redeemProfileRewardPoints(points: number = 50) {
    return this.request<{ success: boolean; data?: any; message?: string }>('/auth/profile/rewards/redeem', {
      method: 'POST',
      body: JSON.stringify({ points }),
    });
  }

  async getGotras() {
    return this.request<{ success: boolean; data?: any; message?: string }>('/auth/profile/gotras');
  }

  async submitGotraRequest(name: string, description?: string) {
    return this.request<{ success: boolean; message?: string; data?: { id: number; name: string; verified: boolean } }>('/auth/profile/gotras', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
  }

  async getGotraRequests() {
    return this.request<{ success: boolean; data?: any; message?: string }>('/gotras/requests');
  }

  async getVerifiedGotras() {
    return this.request<{ success: boolean; data?: any; message?: string }>('/gotras/verified');
  }

  async approveGotra(
    gotraId: number,
    payload?: { name?: string; nameNepali?: string; description?: string }
  ) {
    return this.request<{ success: boolean; message?: string }>(`/gotras/${gotraId}/approve`, {
      method: 'POST',
      body: payload ? JSON.stringify(payload) : undefined,
    });
  }

  async updateGotra(
    gotraId: number,
    payload: { name?: string; nameNepali?: string; description?: string }
  ) {
    return this.request<{ success: boolean; message?: string }>(`/gotras/${gotraId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteGotra(gotraId: number) {
    return this.request<{ success: boolean; message?: string }>(`/gotras/${gotraId}`, {
      method: 'DELETE'
    });
  }

  // ── Astrologers ──────────────────────────────────────────────────────────────
  async getAstrologers(includeInactive = false) {
    const q = includeInactive ? '?include_inactive=true' : '';
    return this.request<{ success: boolean; data?: any[]; message?: string }>(`/astrologers${q}`);
  }

  async createAstrologer(payload: Record<string, any>) {
    return this.request<{ success: boolean; data?: any; message?: string }>('/astrologers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateAstrologer(id: number, payload: Record<string, any>) {
    return this.request<{ success: boolean; data?: any; message?: string }>(`/astrologers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteAstrologer(id: number) {
    return this.request<{ success: boolean; message?: string }>(`/astrologers/${id}`, {
      method: 'DELETE',
    });
  }

  async getCurrentUser() {
    return this.request<{ success: boolean; user?: any; message?: string }>('/auth/me');
  }

  async forgotPassword(email: string) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyOtp(email: string, otp: string) {
    return this.request('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });
  }

  async resetPassword(token: string, newPassword: string, otp?: string, email?: string) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword, otp, email }),
    });
  }

  // Blog endpoints
  async getBlogs(page = 1, limit = 10, category?: string, status = 'published', language?: string, search?: string) {
    let url = `/blog/posts?page=${page}&limit=${limit}&status=${status}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    if (language) url += `&language=${encodeURIComponent(language)}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return this.request(url);
  }

  async getBlogBySlug(slug: string) {
    return this.request(`/blog/posts/${slug}`);
  }

  async getBlogCategories() {
    return this.request('/blog/categories');
  }

  async getBlogStats() {
    return this.request('/blog/stats');
  }

  async submitBlogPost(payload: { title: string; content: string; imageUrl?: string; category?: string; tags?: string; docAttachments?: Array<{ name: string; url: string; id?: string; type?: string }>; authorEmailPublic?: boolean }) {
    return this.request('/blog/submit', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getBlogRewardEngagement() {
    return this.request('/blog/rewards/engagement');
  }

  async applyBlogRewardPoints(payload: {
    blogPostPoints: number;
    likePoints: number;
    commentPoints: number;
    sharePoints: number;
    viewPoints: number;
  }) {
    return this.request('/blog/rewards/apply', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async generateAIBlog(topic: string, options?: { tone?: string; length?: string; targetAudience?: string }) {
    return this.request('/blog/generate', {
      method: 'POST',
      body: JSON.stringify({ topic, ...options }),
    });
  }

  async generateSEO(title: string, content: string, category?: string, tags?: string) {
    return this.request('/blog/generate-seo', {
      method: 'POST',
      body: JSON.stringify({ title, content, category, tags }),
    });
  }

  async getSEOScore(postId: number) {
    return this.request(`/blog/posts/${postId}/seo-score`);
  }

  // Blog image upload to Himalayan Host storage
  // Compress image client-side using Canvas before uploading
  private async compressImage(file: File, maxWidthPx = 1200, qualityJpeg = 0.82): Promise<File> {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxWidthPx / img.naturalWidth);
        const w = Math.round(img.naturalWidth * scale);
        const h = Math.round(img.naturalHeight * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => resolve(blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }) : file),
          'image/jpeg', qualityJpeg
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }

  async uploadBlogImage(file: File): Promise<{ success: boolean; url?: string; filename?: string; error?: string }> {
    try {
      // Compress before upload (max 1200px wide, 82% JPEG quality)
      const compressed = await this.compressImage(file);
      const formData = new FormData();
      formData.append('image', compressed);

      const uploadUrl = `${this.baseURL}/blog/upload`;
      const headers: Record<string, string> = {};
      if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 30000);
      const res = await fetch(uploadUrl, { method: 'POST', headers, body: formData, signal: controller.signal })
        .finally(() => clearTimeout(tid));

      const data = await res.json().catch(() => ({ success: false, error: `HTTP ${res.status}` }));
      return data;
    } catch (err: any) {
      return { success: false, error: err?.message || 'Upload failed' };
    }
  }

  async createBlog(blogData: any) {
    return this.request('/blog/posts', {
      method: 'POST',
      body: JSON.stringify(blogData),
    });
  }

  async updateBlog(id: number, blogData: any) {
    return this.request(`/blog/posts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(blogData),
    });
  }

  async deleteBlog(id: number) {
    return this.request(`/blog/posts/${id}`, {
      method: 'DELETE',
    });
  }

  async restoreBlog(id: number) {
    return this.request(`/blog/posts/${id}/restore`, {
      method: 'POST',
    });
  }

  async bulkDeleteBlogs(postIds: number[]) {
    return this.request('/blog/posts/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ postIds }),
    });
  }

  async bulkRestoreBlogs(postIds: number[]) {
    return this.request('/blog/posts/bulk-restore', {
      method: 'POST',
      body: JSON.stringify({ postIds }),
    });
  }

  async permanentlyDeleteBlog(id: number) {
    return this.request(`/blog/posts/${id}/permanent`, {
      method: 'DELETE',
    });
  }

  async bulkPermanentlyDeleteBlogs(postIds: number[]) {
    return this.request('/blog/posts/bulk-permanent-delete', {
      method: 'POST',
      body: JSON.stringify({ postIds }),
    });
  }

  // Blog interaction endpoints
  async toggleBlogLike(postId: number) {
    return this.request(`/blog/posts/${postId}/like`, {
      method: 'POST',
    });
  }

  async getBlogComments(postId: number) {
    return this.request(`/blog/posts/${postId}/comments`);
  }

  async addBlogComment(postId: number, content: string, parentCommentId?: number) {
    return this.request(`/blog/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content, parentCommentId }),
    });
  }

  async deleteBlogComment(commentId: number) {
    return this.request(`/blog/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  // Profile image upload (Vercel Blob via /uploads/image)
  async uploadProfileImage(file: File, folder: string = 'profiles'): Promise<{ success: boolean; url?: string; error?: string }> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', folder);

    const url = `${this.baseURL}/uploads/image`;
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      const text = await response.text();
      let data: any = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = { success: false, error: text || 'Invalid response' };
      }

      if (!response.ok) {
        return { success: false, error: data?.error || data?.message || `Upload failed with status ${response.status}` };
      }

      return data;
    } catch (error: any) {
      return { success: false, error: error?.message || 'Upload failed' };
    }
  }

  async uploadDriveFile(file: File): Promise<{ success: boolean; url?: string; id?: string; name?: string; error?: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseURL}/uploads/docs`;
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      const text = await response.text();
      let data: any = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = { success: false, error: text || 'Invalid response' };
      }

      if (!response.ok) {
        return { success: false, error: data?.error || data?.message || `Upload failed with status ${response.status}` };
      }

      return data;
    } catch (error: any) {
      return { success: false, error: error?.message || 'Upload failed' };
    }
  }

  // Admin comment management
  async getPendingComments(page = 1, limit = 20) {
    return this.request(`/blog/comments/pending?page=${page}&limit=${limit}`);
  }

  async approveComment(commentId: number) {
    return this.request(`/blog/comments/${commentId}/approve`, {
      method: 'POST',
    });
  }

  async rejectComment(commentId: number) {
    return this.request(`/blog/comments/${commentId}/reject`, {
      method: 'POST',
    });
  }

  async shareBlogPost(postId: number, platform?: string, shareUrl?: string) {
    return this.request(`/blog/posts/${postId}/share`, {
      method: 'POST',
      body: JSON.stringify({ platform, shareUrl }),
    });
  }

  async trackBlogShareVisit(shareToken: string, event: 'opened' | 'qualified', activeViewMs?: number) {
    return this.request('/blog/shares/track', {
      method: 'POST',
      body: JSON.stringify({
        shareToken,
        event,
        activeViewMs,
      }),
    });
  }

  // Panchanga endpoints
  async getPanchanga(date?: string, coords?: { lat: number; lon: number }) {
    const qs = new URLSearchParams();
    if (date) qs.set('date', date);
    if (coords) { qs.set('lat', String(coords.lat)); qs.set('lon', String(coords.lon)); }
    try {
      if (typeof window !== 'undefined' && localStorage.getItem('panchangaDisplayLanguage') === 'english') {
        qs.set('language', 'english');
      }
    } catch (e) {}
    const q = qs.toString();
    return this.request(`/panchanga${q ? '?' + q : ''}`);
  }

  async getAllPanchanga() {
    try {
      if (typeof window !== 'undefined' && localStorage.getItem('panchangaDisplayLanguage') === 'english') {
        return this.request('/panchanga/all?language=english');
      }
    } catch (e) {}
    return this.request('/panchanga/all');
  }

  async getCurrentPanchanga(coords?: { lat: number; lon: number }) {
    const qs = new URLSearchParams();
    if (coords) { qs.set('lat', String(coords.lat)); qs.set('lon', String(coords.lon)); }
    try {
      if (typeof window !== 'undefined' && localStorage.getItem('panchangaDisplayLanguage') === 'english') {
        qs.set('language', 'english');
      }
    } catch (e) {}
    const q = qs.toString();
    return this.request(`/panchanga/current${q ? '?' + q : ''}`);
  }

  async trackUserCrawl(payload: {
    eventType: string;
    pagePath?: string;
    section?: string;
    category?: string;
    serviceId?: string | number;
    action?: string;
    metadata?: Record<string, any>;
  }) {
    const trackingIds = this.getTrackingIdentifiers();
    const deviceInfo = this.getDeviceInfo();
    const enrichedPayload = {
      ...payload,
      metadata: {
        ...(payload.metadata || {}),
        deviceId: payload.metadata?.deviceId || trackingIds.deviceId,
        sessionId: payload.metadata?.sessionId || trackingIds.sessionId,
        deviceType: payload.metadata?.deviceType || deviceInfo.deviceType,
        devicePlatform: payload.metadata?.devicePlatform || deviceInfo.platform,
        deviceOs: payload.metadata?.deviceOs || deviceInfo.os,
        browser: payload.metadata?.browser || deviceInfo.browser,
      },
    };

    return this.request('/user-crawls/track', {
      method: 'POST',
      body: JSON.stringify(enrichedPayload),
    });
  }

  async getUserCrawls(params: { page?: number; limit?: number; eventType?: string; userId?: number; email?: string } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.eventType) query.append('eventType', params.eventType);
    if (params.userId) query.append('userId', String(params.userId));
    if (params.email) query.append('email', params.email);
    const queryString = query.toString();
    return this.request(`/user-crawls/admin${queryString ? `?${queryString}` : ''}`);
  }

  async getUserCrawlSummary() {
    return this.request('/user-crawls/admin/summary');
  }

  async getNewsletterLeads(params: { page?: number; limit?: number; status?: string } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.status) query.append('status', params.status);
    const queryString = query.toString();
    return this.request(`/user-crawls/admin/newsletter-leads${queryString ? `?${queryString}` : ''}`);
  }

  async saveNewsletterLead(email: string, metadata: Record<string, any> = {}) {
    return this.request('/user-crawls/newsletter-lead', {
      method: 'POST',
      body: JSON.stringify({
        email,
        source: 'newsletter_popup',
        metadata,
      }),
    });
  }

  // Appointment endpoints
  async getGurus(specialization?: string) {
    const params = specialization ? `?specialization=${specialization}` : '';
    return this.request(`/appointments/gurus${params}`);
  }

  async getAvailableSlots(guruId: number, date: string) {
    return this.request(`/appointments/gurus/${guruId}/slots/${date}`);
  }

  async getServiceSlots(serviceId: number | string, date: string) {
    return this.request(`/appointments/slots/service/${serviceId}/${date}`);
  }

  async createAppointment(appointmentData: any) {
    return this.request('/appointments/book', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  async getAppointments() {
    return this.request('/appointments');
  }

  async getAppointmentDetails(appointmentId: number) {
    return this.request(`/appointments/${appointmentId}`);
  }

  async createServiceBooking(bookingData: any) {
    return this.request('/services/book', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async rescheduleAppointment(appointmentId: number, newDate: string, newTime: string) {
    return this.request(`/appointments/${appointmentId}/reschedule`, {
      method: 'PUT',
      body: JSON.stringify({ newDate, newTime }),
    });
  }

  async cancelAppointment(appointmentId: number) {
    return this.request(`/appointments/${appointmentId}/cancel`, {
      method: 'PUT',
    });
  }

  async createServiceAppointment(appointmentData: any) {
    return this.request('/appointments/book-service', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  async applyPromoCode(data: { code: string; serviceId: string | number; amount: number }) {
    return this.request('/promocodes/apply', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Admin appointment endpoints
  async getAllAppointments(filters?: { status?: string; serviceId?: number; page?: number; limit?: number }) {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.serviceId) params.append('serviceId', filters.serviceId.toString());
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    const query = params.toString();
    return this.request(`/appointments/admin/all${query ? `?${query}` : ''}`);
  }

  async updateAppointmentStatus(appointmentId: number, status: string, adminNotes?: string, meetingLink?: string) {
    return this.request(`/appointments/admin/${appointmentId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, adminNotes, ...(meetingLink !== undefined ? { meetingLink } : {}) }),
    });
  }

  async adminRescheduleAppointment(appointmentId: number, newDate: string, newTime: string, adminNotes?: string, meetingLink?: string) {
    return this.request(`/appointments/admin/${appointmentId}/reschedule`, {
      method: 'PUT',
      body: JSON.stringify({ newDate, newTime, adminNotes, ...(meetingLink !== undefined ? { meetingLink } : {}) }),
    });
  }

  async markAppointmentPaid(appointmentId: number, amount?: string | number, method?: string, reference?: string) {
    return this.request(`/appointments/admin/${appointmentId}/mark-paid`, {
      method: 'PUT',
      body: JSON.stringify({ amount, method, reference }),
    });
  }

  // Services endpoints
  async getServices(activeOnly = false) {
    const query = activeOnly ? '?active_only=true' : '';
    const endpoint = `/services${query}`;
    const fullUrl = `${this.baseURL}${endpoint}`;
    
    console.log('🔍 getServices called:');
    console.log('   Endpoint:', endpoint);
    console.log('   Base URL:', this.baseURL);
    console.log('   Full URL:', fullUrl);
    
    try {
      // Use a pragmatic timeout for services; a bit higher than before to reduce noisy fallbacks
      const url = `${this.baseURL}${endpoint}`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      const controller = new AbortController();
      // Slightly longer timeout to avoid premature aborts in slower networks
      const timeout = 8000;
      const timeoutId = setTimeout(() => {
        console.warn('⏱️ Services request timeout - aborting after', timeout, 'ms');
        controller.abort();
      }, timeout);

      const response = await fetch(url, {
        headers: headers as HeadersInit,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        const data = text ? JSON.parse(text) : {};
        console.log('✅ getServices success:', data?.success, 'Data count:', data?.data?.length || 0);
        return data;
      }

      return { success: false, data: [] };
    } catch (error: any) {
      // Silently handle timeout - return empty data so fallback is used
      if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        console.warn('⚠️ Services request timed out, using fallback');
        return {
          success: false,
          data: [],
          fallback: true,
          message: 'Request timeout - using fallback services'
        };
      }
      
      console.error('❌ Error fetching services:', error);
      // Return empty array on error so UI doesn't break
      return {
        success: false,
        data: [],
        fallback: true,
        message: error.message || 'Failed to load services'
      };
    }
  }

  async getServiceById(id: number | string) {
    return this.request(`/services/${id}`);
  }

  async createService(serviceData: any) {
    return this.request('/services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async updateService(id: number | string, serviceData: any) {
    console.log('📡 Updating service:', { id, url: `${this.baseURL}/services/${id}` });
    return this.request(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    }, { disableTimeout: true });
  }

  async deleteService(id: number | string) {
    return this.request(`/services/${id}`, {
      method: 'DELETE',
    });
  }

  // Zodiac Signs (Rasi Phal) endpoints
  async getZodiacSigns() {
    return this.request<{ success: boolean; data?: any[]; count?: number; message?: string }>('/zodiac');
  }

  async getZodiacSignById(id: number | string, language: 'en' | 'ne' | 'hi' = 'en', period: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'daily') {
    try {
      const response = await this.request<{ success: boolean; data?: any; message?: string }>(`/zodiac/${id}?language=${language}&period=${period}`);
      return response;
    } catch (error: any) {
      // Handle 404 gracefully - return response object instead of throwing
      if (error?.status === 404 || error?.statusCode === 404 || error?.message?.includes('404') || error?.message?.includes('not found')) {
        return { success: false, data: null, message: 'Zodiac sign details not found' };
      }
      // For other errors, return error response instead of throwing
      return { success: false, data: null, message: error?.message || 'Failed to fetch zodiac sign details' };
    }
  }

  async adminGetUsers(params: { search?: string; status?: string; page?: number; limit?: number } = {}) {
    const query = new URLSearchParams();
    if (params.search) query.append('search', params.search);
    if (params.status && params.status !== 'all') query.append('status', params.status);
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    const queryString = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/user/admin/users${queryString}`);
  }

  async adminUpdateUserStatus(
    userId: number | string,
    payload: { action: 'block' | 'unblock'; reason: string }
  ) {
    return this.request(`/user/admin/users/${userId}/status`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async adminUpdateUserRole(
    userId: number | string,
    payload: { role: 'admin' | 'moderator' | 'user'; reason?: string }
  ) {
    return this.request(`/user/admin/users/${userId}/role`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async adminGetUserActivity(userId: number | string, limit = 20) {
    const query = new URLSearchParams({ limit: limit.toString() }).toString();
    return this.request(`/user/admin/users/${userId}/activity?${query}`);
  }

  async adminDeleteUser(userId: number | string) {
    return this.request(`/user/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async getResendPoolConfig() {
    return this.request('/resend-pool');
  }

  async updateResendPoolSettings(payload: {
    warnThreshold1?: number;
    warnThreshold2?: number;
    enforceDailyLimit?: boolean;
    enforceMonthlyLimit?: boolean;
  }) {
    return this.request('/resend-pool/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async saveResendPoolAccount(payload: {
    name: string;
    apiKey: string;
    fromEmail?: string;
    monthlyLimit?: number;
    dailyLimit?: number;
    warnThreshold1?: number;
    warnThreshold2?: number;
    isActive?: boolean;
  }) {
    return this.request('/resend-pool/accounts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteResendPoolAccount(name: string) {
    return this.request(`/resend-pool/accounts/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }

  async testResendPoolAccount(name: string) {
    return this.request(`/resend-pool/accounts/${encodeURIComponent(name)}/test`, {
      method: 'POST',
    });
  }

  async getResendWebhookStats() {
    return this.request('/resend-pool/webhook-stats');
  }

  async getResendWebhookConfig() {
    return this.request('/resend-pool/webhook-config');
  }

  async updateResendWebhookConfig(payload: {
    webhookId?: string;
    clientToken?: string;
    signingSecret?: string;
  }) {
    return this.request('/resend-pool/webhook-config', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async testResendWebhookConnection(payload?: { webhookId?: string; endpoint?: string }) {
    return this.request('/resend-pool/webhook-test', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  async getResendWebhookConnections() {
    return this.request('/resend-pool/webhook-connections');
  }

  async saveResendWebhookConnection(payload: {
    mailAddress: string;
    endpoint: string;
    clientToken: string;
    signingSecret?: string;
  }) {
    return this.request('/resend-pool/webhook-connections', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteResendWebhookConnection(mailAddress: string) {
    return this.request(`/resend-pool/webhook-connections/${encodeURIComponent(mailAddress)}`, {
      method: 'DELETE',
    });
  }

  async setResendWebhookConnectionConfigured(mailAddress: string, isConfigured: boolean) {
    return this.request(`/resend-pool/webhook-connections/${encodeURIComponent(mailAddress)}/configured`, {
      method: 'PUT',
      body: JSON.stringify({ isConfigured }),
    });
  }

  async testResendWebhookConnectionByMail(mailAddress: string) {
    return this.request('/resend-pool/webhook-test', {
      method: 'POST',
      body: JSON.stringify({ mailAddress }),
    });
  }

  async getZohoPoolConfig() {
    return this.request('/zoho-pool');
  }

  async updateZohoPoolSettings(payload: {
    enforceDailyLimit?: boolean;
    enforceMonthlyLimit?: boolean;
  }) {
    return this.request('/zoho-pool/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async saveZohoPoolAccount(payload: {
    name: string;
    email: string;
    appToken: string;
    fromName?: string;
    smtpHost?: string;
    smtpPort?: number;
    smtpSecure?: boolean;
    imapHost?: string;
    imapPort?: number;
    imapSecure?: boolean;
    monthlyLimit?: number;
    dailyLimit?: number;
    warnThreshold1?: number;
    warnThreshold2?: number;
    isActive?: boolean;
  }) {
    return this.request('/zoho-pool/accounts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteZohoPoolAccount(name: string) {
    return this.request(`/zoho-pool/accounts/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }

  async testZohoPoolAccount(name: string) {
    return this.request(`/zoho-pool/accounts/${encodeURIComponent(name)}/test`, {
      method: 'POST',
    });
  }

  async getZohoTrackerStats() {
    return this.request('/zoho-pool/stats');
  }

  async setZohoAccountConfigured(name: string, isConfigured: boolean) {
    return this.request(`/zoho-pool/accounts/${encodeURIComponent(name)}/configure`, {
      method: 'PUT',
      body: JSON.stringify({ isConfigured }),
    });
  }

  // Contact endpoints
  async sendContactMessage(messageData: { name: string; email: string; phone?: string; message: string }) {
    return this.request('/contact', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  // Category endpoints
  async getCategories() {
    return this.request('/categories');
  }

  async getActiveCategories() {
    return this.request('/categories/active');
  }

  async getCategoryById(id: number) {
    return this.request(`/categories/${id}`);
  }

  async createCategory(categoryData: any) {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async updateCategory(id: number, categoryData: any) {
    return this.request(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  }

  async deleteCategory(id: number) {
    return this.request(`/categories/${id}`, {
      method: 'DELETE',
    });
  }

  async seedCategories() {
    return this.request('/categories/seed', {
      method: 'POST',
    });
  }

  // Time slot endpoints
  async getTimeSlots(serviceId: number | string, date?: string) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/time-slots/service/${serviceId}${params}`);
  }

  async checkVPN() {
    return this.request('/time-slots/check-vpn');
  }

  async getUserTimezone() {
    return this.request('/time-slots/timezone');
  }

  // Rasiphal (Zodiac Predictions) endpoints
  async getRasiphalPredictions(params?: { 
    type?: 'daily' | 'weekly'; 
    zodiac_sign_id?: number; 
    start_date?: string; 
    end_date?: string; 
    is_published?: boolean;
    page?: number; 
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.zodiac_sign_id) queryParams.append('zodiac_sign_id', params.zodiac_sign_id.toString());
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.is_published !== undefined) queryParams.append('is_published', params.is_published.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const query = queryParams.toString();
    return this.request<{ success: boolean; data?: any[]; pagination?: any; message?: string }>(`/rasiphal${query ? `?${query}` : ''}`);
  }

  async getRasiphalPredictionById(id: number | string) {
    return this.request<{ success: boolean; data?: any; message?: string }>(`/rasiphal/${id}`);
  }

  async createRasiphalPrediction(predictionData: any) {
    return this.request<{ success: boolean; data?: any; message?: string }>('/rasiphal', {
      method: 'POST',
      body: JSON.stringify(predictionData),
    });
  }

  async updateRasiphalPrediction(id: number | string, predictionData: any) {
    return this.request<{ success: boolean; data?: any; message?: string }>(`/rasiphal/${id}`, {
      method: 'PUT',
      body: JSON.stringify(predictionData),
    });
  }

  async deleteRasiphalPrediction(id: number | string) {
    return this.request<{ success: boolean; message?: string }>(`/rasiphal/${id}`, {
      method: 'DELETE',
    });
  }

  async translateRasiphalText(text: string, targetLanguage: 'english' | 'hindi') {
    return this.request<{ success: boolean; translated_text?: string; message?: string }>('/rasiphal/translate', {
      method: 'POST',
      body: JSON.stringify({ text, target_language: targetLanguage }),
    });
  }

  // ----- Rasiphal Topics (new topic-based system) -----
  async getRasiphalTopics(activeOnly = false) {
    const qs = activeOnly ? '?active_only=true' : '';
    return this.request<{ success: boolean; data?: any[] }>(`/rasiphal-topics/topics${qs}`);
  }
  async createRasiphalTopic(data: any) {
    return this.request('/rasiphal-topics/topics', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateRasiphalTopic(id: number, data: any) {
    return this.request(`/rasiphal-topics/topics/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteRasiphalTopic(id: number) {
    return this.request(`/rasiphal-topics/topics/${id}`, { method: 'DELETE' });
  }
  async getRasiphalPool(topicId?: number, zodiacSignId?: number) {
    const qs = new URLSearchParams();
    if (topicId) qs.set('topic_id', String(topicId));
    if (zodiacSignId) qs.set('zodiac_sign_id', String(zodiacSignId));
    const q = qs.toString();
    return this.request<{ success: boolean; data?: any[] }>(`/rasiphal-topics/pool${q ? '?' + q : ''}`);
  }
  async createRasiphalPoolEntry(data: any) {
    return this.request('/rasiphal-topics/pool', { method: 'POST', body: JSON.stringify(data) });
  }
  async bulkCreateRasiphalPool(topicId: number, zodiacSignId: number, entries: string[]) {
    return this.request<{ success: boolean; count?: number; message?: string }>(
      '/rasiphal-topics/pool/bulk',
      { method: 'POST', body: JSON.stringify({ topic_id: topicId, zodiac_sign_id: zodiacSignId, entries }) }
    );
  }
  async updateRasiphalPoolEntry(id: number, data: any) {
    return this.request(`/rasiphal-topics/pool/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteRasiphalPoolEntry(id: number) {
    return this.request(`/rasiphal-topics/pool/${id}`, { method: 'DELETE' });
  }
  // ----- Planet Asta (manual planet-combust periods) -----
  async getPlanetAsta() {
    return this.request<{ success: boolean; data?: any[] }>('/planet-asta');
  }
  async getPlanetAstaCurrent(nepaliDate: string) {
    return this.request<{ success: boolean; data?: any[] }>(`/planet-asta/current?date=${encodeURIComponent(nepaliDate)}`);
  }
  async createPlanetAsta(data: any) {
    return this.request('/planet-asta', { method: 'POST', body: JSON.stringify(data) });
  }
  async updatePlanetAsta(id: number, data: any) {
    return this.request(`/planet-asta/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deletePlanetAsta(id: number) {
    return this.request(`/planet-asta/${id}`, { method: 'DELETE' });
  }

  // ----- Store Promocodes -----
  async adminGetStorePromocodes() {
    return this.request<{ success: boolean; data?: any[] }>('/store-promocodes');
  }
  async adminCreateStorePromocode(data: {
    code: string; discountType: 'percent' | 'flat'; discountValue: number;
    category?: string; itemName?: string; usageType: 'general' | 'one-time';
    email?: string; maxUses?: number; expiresAt: string;
  }) {
    return this.request('/store-promocodes', { method: 'POST', body: JSON.stringify(data) });
  }
  async adminDeleteStorePromocode(id: number | string) {
    return this.request(`/store-promocodes/${id}`, { method: 'DELETE' });
  }
  async adminToggleStorePromocode(id: number | string) {
    return this.request(`/store-promocodes/${id}/toggle`, { method: 'PATCH' });
  }
  async adminGetStorePromocodeUses(id: number | string) {
    return this.request<{ success: boolean; data?: any[] }>(`/store-promocodes/${id}/uses`);
  }

  // ----- Admin Promocodes -----
  async adminGetPromocodes() {
    return this.request<{ success: boolean; data?: any[] }>('/promocodes');
  }
  async adminCreatePromocode(data: {
    code: string;
    discountType: 'percent' | 'flat';
    discountValue: number;
    appliesTo: 'all' | 'specific';
    serviceIds?: (string | number)[];
    usageType: 'general' | 'one-time';
    email?: string;
    maxUses?: number;
    expiresAt: string;
  }) {
    return this.request('/promocodes', { method: 'POST', body: JSON.stringify(data) });
  }
  async adminUpdatePromocode(id: number | string, data: {
    code?: string;
    discountType?: 'percent' | 'flat';
    discountValue?: number;
    appliesTo?: 'all' | 'specific';
    serviceIds?: (string | number)[];
    usageType?: 'general' | 'one-time';
    email?: string;
    maxUses?: number;
    expiresAt?: string;
  }) {
    return this.request(`/promocodes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async adminDeletePromocode(id: number | string) {
    return this.request(`/promocodes/${id}`, { method: 'DELETE' });
  }
  async adminTogglePromocode(id: number | string) {
    return this.request(`/promocodes/${id}/toggle`, { method: 'PATCH' });
  }

  async getRasiphalToday(sign: number, period: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'daily') {
    const qs = new URLSearchParams({ sign: String(sign), period });
    return this.request<{ success: boolean; data?: any; period?: string; date_range?: string; pool_size?: number }>(`/rasiphal-topics/today?${qs.toString()}`);
  }

  async getRasiphalPeriod(sign: number, period: 'daily' | 'weekly' | 'monthly' | 'yearly', date?: string) {
    const qs = new URLSearchParams({ sign: String(sign), period });
    if (date) qs.set('date', date);
    return this.request<{ success: boolean; entries?: any[]; period?: string; pool_size?: number }>(`/rasiphal-topics/period?${qs.toString()}`);
  }

  // Aggregated Rasiphal: every topic (health, career, love, lucky number/colour/
  // time, ...) for one sign in a single transit-aware response.
  async getRasiphalAggregate(
    sign: number,
    period: 'daily' | 'weekly' | 'monthly' | 'yearly',
    lang: 'nepali' | 'english' | 'hindi' = 'nepali',
    date?: string,
  ) {
    const qs = new URLSearchParams({ sign: String(sign), period, lang });
    if (date) qs.set('date', date);
    return this.request<{ success: boolean; data?: any }>(`/rasiphal-topics/aggregate?${qs.toString()}`);
  }
}

export const apiService = new ApiService();
